import * as React from 'react'
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { sb } from '@/lib/supabase'

export type ChangeHandler<T extends Record<string, any>> = (
  payload: RealtimePostgresChangesPayload<T>,
) => void

interface Options {
  /** Channel name. Must be unique per logical subscription. */
  channel: string
  table: string
  /** PostgREST-style filter, e.g. `team_id=eq.<uuid>`. */
  filter?: string
  enabled?: boolean
}

export interface RealtimeState {
  /** The channel reached SUBSCRIBED — the socket is up and the server joined it. */
  subscribed: boolean
  /**
   * A change has actually been DELIVERED on this channel. Only this proves the
   * fast path works; see why below.
   */
  live: boolean
}

/**
 * Subscribe to Postgres changes on one table.
 *
 * Realtime re-evaluates RLS per subscriber, so a member only ever receives rows
 * their policies already allow — the filter here is for efficiency, not
 * security.
 *
 * **`subscribed` is not `live`, and the difference is the whole point.** A
 * channel can join happily and then deliver nothing at all: the platform's
 * Postgres runs without `wal2json`, so `postgres_changes` events are never
 * produced even though the WebSocket is up and the server acknowledges the
 * subscription. `e2e-check.mjs` asserts exactly that pair — "channel subscribed"
 * passes while "the message arrived over realtime" fails.
 *
 * Trusting SUBSCRIBED made this worse than having no realtime at all. Callers
 * pair this with `usePoll`, backing the poll off when they believe the fast path
 * is working — so the Discussion page reported "Live", slowed its poll from 4s to
 * 30s, and then received nothing, which meant a teammate's message could take
 * half a minute to appear. The honest fallback was faster than the "working" one.
 *
 * So liveness is earned, not announced: `live` stays false until a change is
 * actually delivered. On a healthy platform the first change arrives by poll and
 * every one after it arrives instantly; on this one, the fast poll simply keeps
 * doing its job.
 */
export function useRealtime<T extends Record<string, any>>(
  { channel, table, filter, enabled = true }: Options,
  onChange: ChangeHandler<T>,
): RealtimeState {
  const [subscribed, setSubscribed] = React.useState(false)
  const [live, setLive] = React.useState(false)

  // Keep the handler in a ref so a re-render doesn't tear down the channel.
  const handlerRef = React.useRef(onChange)
  handlerRef.current = onChange

  React.useEffect(() => {
    if (!enabled) {
      setSubscribed(false)
      setLive(false)
      return
    }

    const ch = sb()
      .channel(channel)
      .on<T>(
        'postgres_changes',
        { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) },
        (payload) => {
          // Delivery is the proof. Latching it here rather than in the caller so
          // every consumer gets the same guarantee.
          setLive(true)
          handlerRef.current(payload)
        },
      )
      .subscribe((status) => {
        setSubscribed(status === 'SUBSCRIBED')
        if (status !== 'SUBSCRIBED') setLive(false)
      })

    return () => {
      setSubscribed(false)
      setLive(false)
      void sb().removeChannel(ch)
    }
  }, [channel, table, filter, enabled])

  return { subscribed, live }
}

/**
 * Call `fn` every `intervalMs` while `enabled`. Pauses when the tab is hidden —
 * a backgrounded portal polling every few seconds for hours is pure waste, and
 * the visibility change triggers an immediate catch-up refresh.
 */
export function usePoll(fn: () => void, intervalMs: number, enabled = true) {
  const fnRef = React.useRef(fn)
  fnRef.current = fn

  React.useEffect(() => {
    if (!enabled) return

    let timer: number | undefined

    const start = () => {
      stop()
      timer = window.setInterval(() => fnRef.current(), intervalMs)
    }
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer)
      timer = undefined
    }

    const onVisibility = () => {
      if (document.hidden) {
        stop()
      } else {
        fnRef.current()
        start()
      }
    }

    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalMs, enabled])
}
